import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import SocialIcons from "@/components/SocialIcons";
import { WhatsAppCTA, WhatsAppGlyph } from "@/components/WhatsApp";
import { SITE, DEFAULT_WA_MESSAGES } from "@/config/site";
import { useSiteConfig } from "@/lib/siteConfig";
import { useSiteSettings } from "@/lib/siteSettings";
import { useAuth } from "@/lib/auth";

/** Public footer — localised via i18n `pub.footer.*`. */
export default function PublicFooter() {
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const { brand } = useSiteSettings();
  const clinicName = brand?.clinicName || SITE.brand;
  const tagline = brand?.tagline || SITE.tagline;
  const { user } = useAuth();
  const waHref = `https://wa.me/${cfg.phoneRaw || SITE.phoneRaw}?text=${encodeURIComponent(DEFAULT_WA_MESSAGES.contact)}`;
  const bookHref = user ? "/app/appointments" : `/register?redirect=${encodeURIComponent("/app/appointments")}`;
  return (
    <footer style={{ borderTop: "1px solid var(--fl-border)", background: "color-mix(in oklab, var(--fl-bg) 92%, transparent)" }} data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <Logo variant="wordmark" size="md" />
          <div className="mt-2 text-xs uppercase tracking-widest" style={{ color: "var(--fl-orange)" }}>{tagline}</div>
          <p className="mt-4 text-sm max-w-md" style={{ color: "var(--fl-muted)" }}>{t("pub.footer.intro")}</p>
          <div className="mt-5 flex items-center gap-4">
            <SocialIcons size={20} testid="footer-socials" />
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Contact ${clinicName} on WhatsApp`}
              data-testid="footer-wa-icon"
              className="transition-colors hover:text-[#25D366]"
              style={{ color: "var(--fl-text-2)" }}
            >
              <WhatsAppGlyph size={20} />
            </a>
          </div>
        </div>

        <div>
          <div className="fl-label mb-3">{t("pub.footer.companyLbl")}</div>
          <ul className="space-y-2 text-sm" style={{ color: "var(--fl-muted)" }}>
            <li><Link to="/team" className="hover:text-[color:var(--fl-orange)] transition-colors">{t("pub.footer.theTeam")}</Link></li>
            <li><Link to="/services" className="hover:text-[color:var(--fl-orange)] transition-colors">{t("pub.footer.services")}</Link></li>
            <li><Link to="/prices" className="hover:text-[color:var(--fl-orange)] transition-colors" data-testid="footer-prices-link">Prices</Link></li>
            <li><Link to="/service-finder" className="hover:text-[color:var(--fl-orange)] transition-colors" data-testid="footer-finder-link">{t("pub.footer.finder")}</Link></li>
            <li><Link to="/resources" className="hover:text-[color:var(--fl-orange)] transition-colors" data-testid="footer-resources-link">{t("pub.footer.resources")}</Link></li>
            <li><Link to="/testimonials" className="hover:text-[color:var(--fl-orange)] transition-colors">{t("pub.footer.testimonials")}</Link></li>
            <li><Link to="/contact" className="hover:text-[color:var(--fl-orange)] transition-colors" data-testid="footer-contact-link">{t("pub.footer.contact")}</Link></li>
            <li><Link to="/trust" className="hover:text-[color:var(--fl-orange)] transition-colors" data-testid="footer-trust-link">Trust & Data Safety</Link></li>
          </ul>
        </div>

        <div>
          <div className="fl-label mb-3">{t("pub.footer.contactLbl")}</div>
          <ul className="space-y-2 text-sm" style={{ color: "var(--fl-muted)" }}>
            <li>
              <a href={`tel:${(cfg.phone || SITE.phone).replace(/\s/g, "")}`} className="inline-flex items-center gap-2 hover:text-[color:var(--fl-orange)] transition-colors">
                <Phone size={12} /> {cfg.phone || SITE.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${cfg.email || SITE.email}`} className="inline-flex items-center gap-2 hover:text-[color:var(--fl-orange)] transition-colors">
                <Mail size={12} /> {cfg.email || SITE.email}
              </a>
            </li>
            <li className="inline-flex items-start gap-2">
              <MapPin size={12} className="mt-0.5" />
              <span>{cfg.address.line1}{cfg.address.city ? `, ${cfg.address.city}` : ""}</span>
            </li>
          </ul>
          <div className="mt-4 flex flex-col gap-2">
            <Link to={bookHref} className="fl-btn text-xs px-3 py-2 rounded-md text-center" data-testid="footer-book">
              {t("pub.footer.bookCta")}
            </Link>
            <WhatsAppCTA message={DEFAULT_WA_MESSAGES.contact} variant="soft" size="sm" testid="footer-wa-cta" fullWidth>
              {t("pub.footer.waCta")}
            </WhatsAppCTA>
          </div>
        </div>
      </div>
      <div className="border-t py-3 px-6 text-[11px] leading-snug" style={{ borderColor: "var(--fl-border)", background: "color-mix(in oklab, #B91C1C 8%, transparent)", color: "var(--fl-text-2)" }} data-testid="emergency-notice-public">
        <b className="text-[color:var(--fl-orange)]">Not an emergency service.</b>{" "}
        If you or someone else is experiencing a medical emergency, call your local emergency services immediately
        (999 in the UK, 112 in the EU, 911 in the US).
      </div>
      <div className="border-t py-4 px-6 flex flex-wrap items-center justify-between gap-2 text-[11px]" style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted-2)" }}>
        <div>© {new Date().getFullYear()} {clinicName}. {t("pub.footer.rights")}</div>
        <div className="italic">{tagline}</div>
      </div>
    </footer>
  );
}
